import { z } from "zod";
import { EmailSchema } from "./auth.js";
import { GstinSchema } from "./gstin.js";
import { PhoneSchema, PincodeSchema, clearable } from "./primitives.js";
import { StateCodeSchema } from "./state-codes.js";
/**
 * Company settings contracts (spec §3 `company`, §7.3, §6 `PUT /company`).
 *
 * THE COMPANY ROW IS A SINGLETON (id = 1) and holds the details that are printed
 * on every legally-issued tax invoice, so two rules shape this file:
 *
 *  1. GSTIN / PAN / bank details are CONFIGURATION, not repo content (§7.3,
 *     CLAUDE.md). They are never seeded and never committed — they arrive
 *     through `PUT /company` from an ADMIN and live only in the database. That
 *     is why this schema is the only place they are described.
 *
 *  2. DERIVED FIELDS ARE NOT ACCEPTED. `state_name` is not in the update schema
 *     at all: §4 makes `company.state_code` the first two characters of the
 *     GSTIN and the state NAME a pure lookup, so the server computes both and
 *     the client cannot disagree with it (§6 server-authority).
 *
 * The document counters are absent for a much sharper reason — see
 * {@link CompanyCountersSchema}.
 */
/* -------------------------------------------------------------------------- */
/* Identifiers printed on the invoice                                          */
/* -------------------------------------------------------------------------- */
/** `ABCDE1234F` — five letters, four digits, a check letter. */
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
/**
 * Permanent Account Number. Uppercase only: a PAN is uppercase by definition and
 * silently "fixing" a lowercase entry would hide a paste from the wrong source.
 */
export const PanSchema = z
    .string()
    .trim()
    .length(10, "PAN must be exactly 10 characters")
    .regex(PAN_REGEX, "PAN format is invalid (expected e.g. ABCDE1234F)");
/** `SBIN0001234` — four bank letters, a fixed `0`, then six branch characters. */
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const IfscSchema = z
    .string()
    .trim()
    .length(11, "IFSC must be exactly 11 characters")
    .regex(IFSC_REGEX, "IFSC format is invalid (expected e.g. SBIN0001234)");
/**
 * Bank account number. Deliberately loose on format — Indian account numbers run
 * from 9 to 18 digits depending on the bank and some co-operative banks use
 * letters — but capped at the `@db.VarChar(30)` column width so a paste that is
 * too long fails here with a readable message instead of at the driver.
 */
export const BankAccountNoSchema = z
    .string()
    .trim()
    .regex(/^[0-9A-Za-z]{6,30}$/, "Bank account number must be 6-30 letters or digits");
/* -------------------------------------------------------------------------- */
/* Financial-year label                                                        */
/* -------------------------------------------------------------------------- */
export const FY_LABEL_REGEX = /^(\d{4})-(\d{2})$/;
/**
 * `2026-27` — the label stored in `company.current_fy` and printed as `26-27/67`
 * on every invoice (§5). The second half must be the year after the first, so a
 * fat-fingered `2026-28` is refused rather than silently producing invoice
 * numbers that no accountant will recognise.
 */
export const FinancialYearLabelSchema = z
    .string()
    .trim()
    .regex(FY_LABEL_REGEX, 'Financial year must look like "2026-27"')
    .refine((value) => {
    const match = FY_LABEL_REGEX.exec(value);
    if (match === null)
        return false;
    const startYear = Number(match[1]);
    const endYear = Number(match[2]);
    return endYear === (startYear + 1) % 100;
}, { message: 'The second half of a financial year is the following year, e.g. "2026-27"' });
/* -------------------------------------------------------------------------- */
/* PUT /company                                                                */
/* -------------------------------------------------------------------------- */
/** Trimmed, length-capped free text. */
function text(max) {
    return z
        .string()
        .trim()
        .max(max, `Must be ${max} characters or fewer`);
}
/** A field that must not be blanked when it is sent. */
function required(max, label) {
    return text(max).min(1, `${label} is required`).optional();
}
export const CompanyUpdateBaseSchema = z.object({
    legalName: required(191, "Legal name"),
    tradeName: required(191, "Trade name"),
    tagline: clearable(text(500)),
    /** §7.3 — entered here, never seeded, never committed. */
    gstin: clearable(GstinSchema),
    pan: clearable(PanSchema),
    addressLine1: required(500, "Address line 1"),
    addressLine2: clearable(text(500)),
    city: required(100, "City"),
    /**
     * Only consulted while the company has NO GSTIN. Once a GSTIN is set, §4 makes
     * its first two characters the state code and this value is ignored — the
     * response says so in `warnings`.
     */
    stateCode: StateCodeSchema.optional(),
    pincode: PincodeSchema.optional(),
    phone: PhoneSchema.optional(),
    email: EmailSchema.optional(),
    bankName: clearable(text(191)),
    bankAccountNo: clearable(BankAccountNoSchema),
    bankIfsc: clearable(IfscSchema),
    bankBranch: clearable(text(191)),
    logoUrl: clearable(text(500)),
    signatureUrl: clearable(text(500)),
    termsDefault: clearable(text(5000)),
    jurisdictionNote: clearable(text(255)),
    computerGeneratedNote: clearable(text(255)),
    declarationDefault: clearable(text(5000)),
});
/**
 * `PUT /company` (ADMIN only — spec §2: the OWNER cannot change company or tax
 * settings).
 *
 * Every field is optional and only the keys actually sent are written, so the
 * ADMIN screen can save one section at a time. `null` (or `""`) CLEARS a
 * nullable column; an absent key leaves it untouched. The two are genuinely
 * different operations and the schema keeps them distinguishable.
 *
 * `.strict()` rather than zod's default key-stripping: sending
 * `nextInvoiceSeq` here must be a loud 400, not a silent no-op. A silent drop is
 * the worst outcome — the ADMIN would believe the invoice counter had moved and
 * would only find out when the next invoice came out with the wrong number.
 */
export const CompanyUpdateSchema = CompanyUpdateBaseSchema.strict("Unrecognised company field. The document counters and financial-year markers (nextInvoiceSeq, nextQuoteSeq, currentFy, nextChallanSeq, challanFy, nextCreditNoteSeq, creditNoteFy) are changed only through PUT /company/counters, and stateName is derived from the GSTIN (spec §4, §5).").superRefine((value, ctx) => {
    if (Object.keys(value).length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Send at least one field to update",
        });
    }
});
/* -------------------------------------------------------------------------- */
/* PUT /company/counters                                                       */
/* -------------------------------------------------------------------------- */
/**
 * A sane ceiling for a hand-entered counter. The real numbers are in the tens
 * (invoice 67) and the hundreds (quotation 542); six digits is far past anything
 * this business will reach and catches a mis-paste of a phone number or a GSTIN
 * fragment into the field.
 */
export const MAX_COUNTER_SEQ = 1_000_000;
function seq(label) {
    return z
        .number()
        .int(`${label} must be a whole number`)
        .min(1, `${label} must be at least 1`)
        .max(MAX_COUNTER_SEQ, `${label} looks wrong — the limit is ${MAX_COUNTER_SEQ}`)
        .optional();
}
/**
 * `PUT /company/counters` — capability `invoice.set_counter` (spec §6.1), held
 * by OWNER and ADMIN. Spec §6, "Migration note": the screen that sets the
 * starting counters so the switchover from the manual Word/Excel process
 * happens without a gap or a duplicate.
 *
 * THE DIRECTION RULE IS NOT IN THIS SCHEMA, and cannot be: "forward" is only
 * knowable against the value currently stored, which the client does not hold
 * authoritatively. §6.1 lets the OWNER move a counter FORWARD only and the ADMIN
 * move it either way; the server enforces it against the locked row
 * (`backwardCounterMoves` in apps/api/src/routes/company.ts) and the client
 * predicts the same answer with `canSetCounter` from ./permissions.js.
 *
 * THIS IS THE MOST DANGEROUS ENDPOINT IN THE SYSTEM. Every other write can be
 * corrected; this one can cause two different invoices to carry the same legally
 * issued number, which is a GST compliance failure that cannot be un-issued.
 * Hence three deliberate obstacles that no other endpoint has:
 *
 *   - `confirm: true` must be typed explicitly. It has no default and no
 *     truthy coercion, so no client can wander into a counter change.
 *   - `reason` is REQUIRED and lands in the audit log. Six months later "why is
 *     there a gap between 66 and 80" has an answer written down.
 *   - at least one counter must actually be named, so an empty confirm-only body
 *     cannot produce a reassuring 200 that changed nothing.
 */
/**
 * WHICH `company` COLUMNS EACH NUMBERED SERIES OWNS — the canonical list, and
 * the reason this endpoint can no longer fall behind the allocator.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS A `Record<DocumentKind, …>`, SO A FIFTH DOCUMENT TYPE IS A COMPILE ERROR
 * HERE, in the same way `DOCUMENT_SERIES` in the API's numbering service already
 * is. That symmetry is the whole point of the 2026-08-17 fix: the allocator was
 * made a `Record` over the kind union so a missing series could not compile,
 * while this endpoint stayed a hand-written triple — and quietly fell THREE
 * series behind, leaving `challan_fy` and `credit_note_fy` writable by nothing
 * at all.
 *
 * WHAT THAT COST. Both markers RESET each April, so one forward-dated document
 * advances one of them permanently and every correctly dated document for the
 * rest of the financial year is refused 409. The numbering service points at
 * this screen as THE recovery, in two separate comments — and for three of the
 * four series that promise was false. Recovery meant `UPDATE company SET
 * credit_note_fy = '2026-27'` typed into MariaDB on the live box, which is
 * exactly the outcome `refuseBackwardMove` exists to avoid.
 *
 * `fy: null` FOR QUOTATIONS IS NOT AN OMISSION. That series is continuous — it
 * never resets, never writes a marker, and has no closed year to reopen; the
 * `/YY` on `QOT/542/26` comes from the document's own date. Giving it a marker
 * to edit would invent a control with nothing behind it.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export const DOCUMENT_SERIES_COUNTERS = {
    invoice: { seq: "nextInvoiceSeq", fy: "currentFy" },
    quotation: { seq: "nextQuoteSeq", fy: null },
    challan: { seq: "nextChallanSeq", fy: "challanFy" },
    creditNote: { seq: "nextCreditNoteSeq", fy: "creditNoteFy" },
};
/**
 * The counter fields, as Zod.
 *
 * `satisfies Record<CounterField, …>` IS THE GUARD. A fifth series widens
 * {@link CounterField}, and this object is then missing a key and does not
 * compile — which is precisely what did NOT happen when the challan and
 * credit-note series were added.
 */
const CounterFieldSchemas = {
    nextInvoiceSeq: seq("nextInvoiceSeq"),
    nextQuoteSeq: seq("nextQuoteSeq"),
    nextChallanSeq: seq("nextChallanSeq"),
    nextCreditNoteSeq: seq("nextCreditNoteSeq"),
    currentFy: FinancialYearLabelSchema.optional(),
    challanFy: FinancialYearLabelSchema.optional(),
    creditNoteFy: FinancialYearLabelSchema.optional(),
};
/** Every editable counter name, for the "name at least one" refusal below. */
export const COUNTER_FIELDS = Object.keys(CounterFieldSchemas);
export const CompanyCountersSchema = z
    .object({
    ...CounterFieldSchemas,
    reason: z
        .string()
        .trim()
        .min(3, "Give a reason — it is written to the audit log (spec §11A)")
        .max(500, "Reason must be 500 characters or fewer"),
    confirm: z.literal(true, {
        errorMap: () => ({
            message: "Send confirm: true to override the document counters. Getting this wrong duplicates or skips invoice numbers that have already been issued (spec §5).",
        }),
    }),
})
    .strict()
    .superRefine((value, ctx) => {
    // Derived from the same map, so a new series is covered by this refusal the
    // moment it exists rather than the next time someone remembers to add it.
    const named = COUNTER_FIELDS.some((field) => value[field] !== undefined);
    if (!named) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Name at least one of ${COUNTER_FIELDS.join(", ")}`,
        });
    }
});
//# sourceMappingURL=company.js.map